import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDevMenu, setShowDevMenu] = useState(false);
  const [devUsers, setDevUsers] = useState([]);
  const [devLoading, setDevLoading] = useState(false);
  const { login, loginWithToken } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Check if we're on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  useEffect(() => {
    if (isLocalhost) {
      fetchDevUsers();
    }
  }, [isLocalhost]);

  const fetchDevUsers = async () => {
    try {
      setDevLoading(true);
      const response = await api.get('/auth/dev-users');
      const users = response.data;
      
      // Sort users: allanimaahdi@gmail.com first, then admins, then others
      const targetEmail = 'allanimaahdi@gmail.com';
      const sortedUsers = users.sort((a, b) => {
        // Put allanimaahdi@gmail.com first
        if (a.email === targetEmail) return -1;
        if (b.email === targetEmail) return 1;
        
        // Then admins
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (b.role === 'admin' && a.role !== 'admin') return 1;
        
        // Then sort by email
        return a.email.localeCompare(b.email);
      });
      
      setDevUsers(sortedUsers);
    } catch (error) {
      // Silently fail - dev endpoint might not be available
      console.log('Dev users not available', error);
    } finally {
      setDevLoading(false);
    }
  };

  const handleDevLogin = async (userEmail) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/dev-login', { email: userEmail });
      const { token, user } = response.data;

      // Store token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update auth context
      if (loginWithToken) {
        await loginWithToken(token, user);
      }

      // Redirect to home
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.error || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') setEmail(value);
    if (name === 'password') setPassword(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      } else {
        setError(t('login.error'));
      }
    } catch (err) {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('login.title')}</h1>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('login.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleChange}
              required
              placeholder="votre@email.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('login.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handleChange}
              required
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <p className="auth-link" style={{ marginTop: '0', marginBottom: '1rem', textAlign: 'right' }}>
            <Link to="/forgot-password">{t('login.forgot')}</Link>
          </p>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <p className="auth-link">
          {t('login.notMember')} <Link to="/register">{t('login.register')}</Link>
        </p>

        {/* Dev Menu - Only show on localhost */}
        {isLocalhost && (
          <div className="dev-menu" style={{
            marginTop: '2rem',
            padding: '1rem',
            background: '#fff3cd',
            border: '2px dashed #ffc107',
            borderRadius: '8px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: 0, color: '#856404', fontSize: '1rem' }}>
                {t('login.devMode')}
              </h3>
              <button
                onClick={() => setShowDevMenu(!showDevMenu)}
                style={{
                  background: 'transparent',
                  border: '1px solid #856404',
                  color: '#856404',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                {showDevMenu ? t('login.hide') : t('login.show')}
              </button>
            </div>
            
            {showDevMenu && (
              <div>
                <p style={{ margin: '0 0 1rem 0', color: '#856404', fontSize: '0.9rem' }}>
                  {t('login.devHint')}
                </p>
                {devLoading ? (
                  <p style={{ color: '#856404' }}>{t('login.devLoading')}</p>
                ) : devUsers.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '0.5rem'
                  }}>
                    {devUsers.map((user) => (
                      <button
                        key={user._id || user.email}
                        onClick={() => handleDevLogin(user.email)}
                        disabled={loading}
                        style={{
                          padding: '0.8rem',
                          background: user.role === 'admin' ? '#000' : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem',
                          textAlign: 'left',
                          opacity: loading ? 0.6 : 1
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>{user.email}</div>
                        <div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>
                          {user.firstName} {user.lastName}
                          {user.role === 'admin' && ' (Admin)'}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#856404' }}>{t('login.devNone')}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;


